// The one game store, and every action that changes it.

import type { CasterId } from '../content/casters';
import type { Move, Side } from '../engine/types';
import * as flow from '../game/flow';
import {
  applyAction,
  type MatchAction,
  type MatchRecord,
  type RecordedAction,
  replayMatch,
} from '../game/replay';
import type { GameState, MatchMode } from '../game/state';
import { createRng, type Rng } from '../lib/rng';
import { createStore } from '../lib/store';
import { recordAction, resetHistory, restoreHistory } from './history';

let rng: Rng = createRng(1);
let actions: readonly RecordedAction[] = [];
/** The casters of violet, when a person picked them. See `MatchRecord`. */
let opponentCasters: readonly CasterId[] | null = null;

/** Gets each action of this screen with its place in the record. A room sends it to the server. */
type ActionSink = (index: number, action: RecordedAction) => void;
let sink: ActionSink | null = null;

export const gameStore = createStore<GameState>(flow.createMatch(1, rng));
resetHistory(gameStore.get());

/** Puts an action that changed the match in the record, the store, and the history. */
const keep = (before: GameState, after: GameState, recorded: RecordedAction): void => {
  actions = [...actions, recorded];
  gameStore.set(after);
  recordAction(before, after);
};

/**
 * Runs an action on the store, and gives it to the record and the history. In a
 * room the action shows at once, and the server gets it. The server has the same
 * rules, so it gets the same match.
 */
const act = (action: MatchAction): void => {
  const before = gameStore.get();
  const rngState = rng.state();
  const after = applyAction(before, action, rng);
  if (after === before) return;
  const recorded = { ...action, rngState };
  keep(before, after, recorded);
  sink?.(actions.length - 1, recorded);
};

/** In a room, the actions of this screen go to `next`. Null ends that. */
export const setActionSink = (next: ActionSink | null): void => {
  sink = next;
};

/** An action from the room server, from the other screen. False when it does not fit the match. */
export const applyRecorded = (recorded: RecordedAction): boolean => {
  const before = gameStore.get();
  rng.restore(recorded.rngState);
  const after = applyAction(before, recorded, rng);
  if (after === before) return false;
  keep(before, after, recorded);
  return true;
};

/** How many actions the record has, and the action at a place of it. */
export const actionCount = (): number => actions.length;
export const actionAt = (index: number): RecordedAction | undefined => actions[index];

/**
 * `casters` are the casters of the player in Godsmode. With none, the match
 * draws them. `opponent` are the casters of violet, from a room.
 */
export const newMatch = (
  seed: number,
  mode: MatchMode,
  casters: readonly CasterId[] | null = null,
  opponent: readonly CasterId[] | null = null,
): void => {
  rng = createRng(seed);
  actions = [];
  opponentCasters = opponent;
  gameStore.set(flow.createMatch(seed, rng, mode, casters, opponent));
  resetHistory(gameStore.get());
};

/** Everything a reload needs to get the match back. */
export const matchRecord = (): MatchRecord => {
  const { seed, mode, casters } = gameStore.get();
  const record: MatchRecord = { seed, mode, casters: casters?.gold ?? null, actions };
  return opponentCasters ? { ...record, opponentCasters } : record;
};

/**
 * Replays a record into the store. False when the record does not fit the
 * rules. `beforeSet` gets the replayed state before the store's subscribers do.
 */
export const loadMatch = (record: MatchRecord, beforeSet: (state: GameState) => void): boolean => {
  const replayRng = createRng(record.seed);
  const replayed = replayMatch(record, replayRng);
  if (!replayed) return false;
  beforeSet(replayed.state);
  rng = replayRng;
  actions = record.actions;
  opponentCasters = record.opponentCasters ?? null;
  gameStore.set(replayed.state);
  restoreHistory(replayed.steps, replayed.liveIsAhead);
  return true;
};

export const rollDie = (): void => act({ kind: 'roll' });

export const aimAt = (square: number): void => act({ kind: 'aim', square });

export const castSlot = (slot: number): void => act({ kind: 'cast', slot });

export const cancelCast = (): void => act({ kind: 'uncast' });

export const playMove = (move: Move): void => act({ kind: 'move', move });

export const concede = (side: Side): void => act({ kind: 'concede', side });

export const matchRng = (): Rng => rng;
